import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import API from '../../utils/api';
import {
  FiClock, FiCheckCircle, FiXCircle, FiTruck,
  FiCalendar, FiMapPin, FiAlertTriangle, FiPlus,
  FiChevronLeft, FiChevronRight, FiRefreshCw, FiPrinter, FiCreditCard
} from 'react-icons/fi';

const CustomerBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [, setError] = useState(null);
  const [runtimeMidtransClientKey, setRuntimeMidtransClientKey] = useState(
    process.env.REACT_APP_MIDTRANS_CLIENT_KEY || ''
  );
  const [runtimeMidtransIsProduction, setRuntimeMidtransIsProduction] = useState(
    process.env.REACT_APP_MIDTRANS_IS_PRODUCTION === 'true'
  );

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [pagination, setPagination] = useState({
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    hasNextPage: false,
    hasPreviousPage: false
  });

  const location = useLocation();

  useEffect(() => {
    const fetchMidtransConfig = async () => {
      try {
        const response = await API.get('/api/booking/payments/midtrans/config');

        if (response.data?.client_key) {
          setRuntimeMidtransClientKey(response.data.client_key);
        }

        if (typeof response.data?.is_production === 'boolean') {
          setRuntimeMidtransIsProduction(response.data.is_production);
        }
      } catch (configError) {
        console.error('Error fetching Midtrans config:', configError);
      }
    };

    fetchMidtransConfig();
  }, []);

  const inspectBookingData = useCallback((bookingData) => {
    if (!bookingData) return;

    console.log('Booking raw data inspection:');
    console.log('- ID:', bookingData.id);
    console.log('- date fields:', {
      booking_date: bookingData.booking_date,
      date: bookingData.date,
      formatted: bookingData.booking_date || bookingData.date
    });
    console.log('- time fields:', {
      booking_time: bookingData.booking_time,
      time: bookingData.time,
      formatted: bookingData.booking_time || bookingData.time
    });
    console.log('- service:', bookingData.service_name);
    console.log('- service_price:', bookingData.service_price);
    console.log('- status:', bookingData.status);
    console.log('- all_services:', bookingData.all_services);
  }, []);

  const fetchBookings = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      // Add status filter if not 'all'
      const params = {
        page,
        pageSize,
        ...(activeTab !== 'all' && { status: activeTab })
      };

      console.log(`Fetching bookings with params:`, params);
      const response = await API.get('/api/booking/customer/my-bookings', { params });

      console.log('Booking data from API:', response.data);

      if (response.data && response.data.bookings && response.data.bookings.length > 0) {
        // Inspect first booking to help with debugging
        inspectBookingData(response.data.bookings[0]);
      }

      if (!response.data || !response.data.bookings || response.data.bookings.length === 0) {
        console.log('No bookings found');
        setBookings([]);
        setPagination({
          totalItems: 0,
          totalPages: 0,
          currentPage: 1,
          hasNextPage: false,
          hasPreviousPage: false
        });
        setLoading(false);
        return;
      }

      // Update pagination state
      setPagination(response.data.pagination);

      // Format the booking data
      const formattedBookings = response.data.bookings.map(booking => {
        if (!booking) {
          console.error('Null booking object found in response');
          return null;
        }

        // Ensure we have date and time (checking all possible field names)
        const bookingDate = booking.date || booking.booking_date;
        const bookingTime = booking.time || booking.booking_time;

        console.log(`Processing booking ${booking.id}: `, {
          date: bookingDate,
          time: bookingTime
        });

        // Determine mechanic name with better fallback
        let mechanicName = 'Belum ditugaskan';
        if (booking.mechanic_name) {
          mechanicName = booking.mechanic_name;
        } else if (booking.mechanic_id) {
          mechanicName = `Montir #${booking.mechanic_id}`;
        }

        // Extract individual vehicle parts instead of combining them
        const vehicleMake = booking.make || '';
        const vehicleModel = booking.model || '';
        const vehicleYear = booking.year || '';
        const vehicleLicensePlate = booking.license_plate || '';

        // Get customer name
        const customerName = booking.customer_name || booking.name || 'Nama Pelanggan';

        console.log('Customer name from booking:', {
          id: booking.id,
          customer_name: booking.customer_name,
          name: booking.name,
          final: customerName
        });

        // Format date to Indonesian locale if needed
        let formattedDate = bookingDate;
        if (bookingDate && typeof bookingDate === 'string' && bookingDate.includes('-')) {
          const dateParts = bookingDate.split('-');
          if (dateParts.length === 3) {
            formattedDate = `${dateParts[2]}/${dateParts[1]}/${dateParts[0]}`;
          }
        }

        // Generate services array
        let services = [];
        let totalPrice = parseFloat(booking.service_price || 0);

        if (booking.all_services && booking.all_services.length > 0) {
          // Use the combined services from backend
          services = booking.all_services;
          console.log(`Booking ${booking.id} has combined services:`, services);

          // If we have total_services_price, use that
          if (booking.total_services_price) {
            totalPrice = parseFloat(booking.total_services_price);
          }
          // Otherwise, if we have all_services_prices, calculate total
          else if (booking.all_services_prices && booking.all_services_prices.length === booking.all_services.length) {
            totalPrice = booking.all_services_prices.reduce((sum, price) => sum + parseFloat(price || 0), 0);
          }

          console.log(`Booking ${booking.id} total price:`, totalPrice);
        } else if (booking.service_name) {
          // Single service
          services = [booking.service_name];
        }

        return {
          id: booking.id,
          service: booking.service_name || 'Layanan tidak tersedia',
          services: services,
          related_bookings: booking.related_bookings || [],
          date: formattedDate || 'Tanggal tidak tersedia',
          time: bookingTime || 'Waktu tidak tersedia',
          status: booking.status || 'pending',
          mechanicName: mechanicName,
          customerName: customerName,
          // Individual vehicle fields
          make: booking.make || '',
          model: booking.model || '',
          year: booking.year || '',
          license_plate: booking.license_plate || '',
          vehicleMake: booking.make || '',
          vehicleModel: booking.model || '',
          vehicleYear: booking.year || '',
          vehicleLicensePlate: booking.license_plate || '',
          // Keep vehicle string for other parts of the UI
          vehicle: [vehicleMake, vehicleModel, vehicleYear, vehicleLicensePlate].filter(Boolean).join(' '),
          price: totalPrice,
          paymentId: booking.payment_id || null,
          paymentMethod: booking.payment_method || booking.payment_type || '',
          paymentStatus: booking.payment_status || '',
          isDownPayment: Boolean(booking.is_down_payment),
          downPaymentPercent: booking.down_payment_percent || 0,
          downPaymentAmount: parseFloat(booking.down_payment_amount || 0),
          grossAmount: parseFloat(booking.gross_amount || 0),
          snapToken: booking.snap_token || '',
          redirectUrl: booking.snap_redirect_url || '',
          paymentDate: booking.payment_date || '',
          midtransOrderId: booking.midtrans_order_id || '',
          notes: booking.notes || '',
          location: 'Jl. Raya Serpong No. 123, Tangerang Selatan', // Default location
          createdAt: booking.created_at || new Date().toISOString(),
          completedAt: booking.completed_at || '',
          canceledAt: booking.canceled_at || ''
        };
      }).filter(booking => booking !== null);

      console.log('Formatted bookings:', formattedBookings);

      setBookings(formattedBookings);
      setCurrentPage(page);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setError('Gagal memuat data booking. ' + (error.response?.data?.message || 'Silakan coba lagi nanti.'));
      setLoading(false);
    }
  }, [activeTab, inspectBookingData, pageSize]);

  useEffect(() => {
    fetchBookings(1); // Reset to page 1 when tab changes
  }, [fetchBookings]);

  // Add effect to refresh when navigating from book page
  useEffect(() => {
    // Check if we're coming from the booking page
    if (location.state && location.state.fromBooking) {
      console.log('Detected navigation from booking page, refreshing data...');
      fetchBookings(1);
    }
  }, [fetchBookings, location]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Add effect to fetch bookings when page changes
  useEffect(() => {
    if (currentPage > 0) {
      fetchBookings(currentPage);
    }
  }, [currentPage, fetchBookings]);

  const handleRefresh = () => {
    fetchBookings(currentPage);
  };

  const handlePageSizeChange = (e) => {
    const newPageSize = parseInt(e.target.value);
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const handleCancelBooking = async (bookingId) => {
    try {
      setLoading(true);
      await API.put(`/api/booking/${bookingId}/cancel`);
      setIsModalOpen(false);
      fetchBookings(currentPage); // Refresh data on current page
    } catch (error) {
      console.error('Error canceling booking:', error);
      alert('Gagal membatalkan booking. Silakan coba lagi.');
      setIsModalOpen(false);
      setLoading(false);
    }
  };

  const openBookingDetails = (booking) => {
    console.log('Opening booking details with data:', {
      id: booking.id,
      customerName: booking.customerName,
      customer_name: booking.customer_name,
      name: booking.name
    });
    setSelectedBooking(booking);
    setIsModalOpen(true);
  };

  const ensureSnapScriptLoaded = useCallback(async () => {
    if (!runtimeMidtransClientKey) {
      throw new Error('Midtrans Client Key belum tersedia.');
    }

    if (window.snap) {
      return;
    }

    await new Promise((resolve, reject) => {
      const scriptId = 'midtrans-snap-script-bookings';
      const scriptSrc = runtimeMidtransIsProduction
        ? 'https://app.midtrans.com/snap/snap.js'
        : 'https://app.sandbox.midtrans.com/snap/snap.js';
      const existingScript = document.getElementById(scriptId);

      if (existingScript) {
        existingScript.addEventListener('load', resolve, { once: true });
        existingScript.addEventListener('error', () => reject(new Error('Gagal memuat script Midtrans Snap.')), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.id = scriptId;
      script.type = 'text/javascript';
      script.src = scriptSrc;
      script.setAttribute('data-client-key', runtimeMidtransClientKey);
      script.async = true;
      script.onload = resolve;
      script.onerror = () => reject(new Error('Gagal memuat script Midtrans Snap.'));
      document.body.appendChild(script);
    });
  }, [runtimeMidtransClientKey, runtimeMidtransIsProduction]);

  const handlePrintInvoice = useCallback((booking) => {
    const printWindow = window.open('', '_blank', 'width=900,height=700');

    if (!printWindow) {
      return;
    }

    const serviceList = booking.services && booking.services.length > 0
      ? booking.services.map((service) => `<li>${service}</li>`).join('')
      : `<li>${booking.service || '-'}</li>`;

    const amountToShow = booking.isDownPayment && booking.downPaymentAmount > 0
      ? booking.downPaymentAmount
      : booking.grossAmount || booking.price || 0;

    const outstandingAmount = Math.max((booking.price || 0) - (booking.downPaymentAmount || 0), 0);

    printWindow.document.write(`
      <html>
        <head>
          <title>Invoice ${booking.id}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 32px; color: #1f2937; }
            h1, h2, h3, p { margin: 0; }
            .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; }
            .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 16px; margin-bottom: 16px; }
            .grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
            .label { font-size: 12px; color: #6b7280; margin-bottom: 4px; }
            .value { font-size: 14px; font-weight: 600; }
            ul { margin: 8px 0 0 18px; padding: 0; }
            .total { font-size: 18px; font-weight: 700; color: #ea580c; }
          </style>
        </head>
        <body>
          <div class="header">
            <div>
              <h1>Invoice Booking</h1>
              <p>XSignature Auto Garage</p>
            </div>
            <div style="text-align:right">
              <div class="label">Booking ID</div>
              <div class="value">${booking.id}</div>
            </div>
          </div>

          <div class="card">
            <h3>Layanan</h3>
            <ul>${serviceList}</ul>
          </div>

          <div class="card">
            <div class="grid">
              <div><div class="label">Pelanggan</div><div class="value">${booking.customerName || '-'}</div></div>
              <div><div class="label">Montir</div><div class="value">${booking.mechanicName || '-'}</div></div>
              <div><div class="label">Tanggal</div><div class="value">${booking.date || '-'}</div></div>
              <div><div class="label">Waktu</div><div class="value">${booking.time || '-'}</div></div>
              <div><div class="label">Kendaraan</div><div class="value">${[booking.vehicleMake, booking.vehicleModel, booking.vehicleYear].filter(Boolean).join(' ') || '-'}</div></div>
              <div><div class="label">Plat Nomor</div><div class="value">${booking.vehicleLicensePlate || '-'}</div></div>
            </div>
          </div>

          <div class="card">
            <div class="grid">
              <div><div class="label">Status Booking</div><div class="value">${booking.status || '-'}</div></div>
              <div><div class="label">Status Pembayaran</div><div class="value">${booking.paymentStatus || 'belum ada'}</div></div>
              <div><div class="label">Metode Pembayaran</div><div class="value">${booking.paymentMethod || 'Midtrans Snap'}</div></div>
              <div><div class="label">Tanggal Pembayaran</div><div class="value">${booking.paymentDate ? new Date(booking.paymentDate).toLocaleString('id-ID') : '-'}</div></div>
            </div>
          </div>

          <div class="card">
            <div class="label">Total Tagihan</div>
            <div class="total">${formatPrice(booking.price || 0)}</div>
            ${booking.isDownPayment ? `<p style="margin-top:8px">DP ${booking.downPaymentPercent}%: ${formatPrice(booking.downPaymentAmount || 0)}</p>` : ''}
            <p style="margin-top:8px">Tagihan transaksi ini: ${formatPrice(amountToShow)}</p>
            ${booking.isDownPayment ? `<p style="margin-top:8px">Sisa pembayaran: ${formatPrice(outstandingAmount)}</p>` : ''}
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  }, []);

  const handleRepayBooking = useCallback(async (booking) => {
    try {
      if (booking.snapToken) {
        await ensureSnapScriptLoaded();
        window.snap.pay(booking.snapToken, {
          onSuccess: () => fetchBookings(currentPage),
          onPending: () => fetchBookings(currentPage),
          onClose: () => {}
        });
        return;
      }

      if (booking.redirectUrl) {
        window.location.href = booking.redirectUrl;
        return;
      }

      alert('Link pembayaran belum tersedia untuk booking ini.');
    } catch (repayError) {
      console.error('Error reopening payment:', repayError);
      alert('Gagal membuka pembayaran. Silakan coba lagi.');
    }
  }, [currentPage, ensureSnapScriptLoaded, fetchBookings]);

  const shouldShowPayButton = (booking) => {
    if (!booking) return false;
    if (!booking.snapToken && !booking.redirectUrl) return false;

    const normalizedPaymentStatus = String(booking.paymentStatus || '').toLowerCase();
    return !['approved', 'paid'].includes(normalizedPaymentStatus);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <span className="flex items-center text-yellow-600 bg-yellow-50 px-1.5 py-0.5 rounded text-xs">
            <FiClock className="mr-1" size={12} /> Menunggu Konfirmasi
          </span>
        );
      case 'confirmed':
        return (
          <span className="flex items-center text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded text-xs">
            <FiCalendar className="mr-1" size={12} /> Terjadwal
          </span>
        );
      case 'in-progress':
        return (
          <span className="flex items-center text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded text-xs">
            <FiTruck className="mr-1" size={12} /> Sedang Dikerjakan
          </span>
        );
      case 'completed':
        return (
          <span className="flex items-center text-green-600 bg-green-50 px-1.5 py-0.5 rounded text-xs">
            <FiCheckCircle className="mr-1" size={12} /> Selesai
          </span>
        );
      case 'cancelled':
      case 'canceled':
        return (
          <span className="flex items-center text-red-600 bg-red-50 px-1.5 py-0.5 rounded text-xs">
            <FiXCircle className="mr-1" size={12} /> Dibatalkan
          </span>
        );
      default:
        return (
          <span className="flex items-center text-gray-600 bg-gray-50 px-1.5 py-0.5 rounded text-xs">
            <FiClock className="mr-1" size={12} /> Status Tidak Diketahui
          </span>
        );
    }
  };

  const formatPrice = (price) => {
    if (!price || isNaN(price)) {
      return 'Rp-';
    }
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const renderBookingModal = () => {
    if (!selectedBooking) return null;

    console.log('Selected booking for modal:', selectedBooking);

    return (
      <div className="bg-white rounded-lg p-5 max-w-md w-full max-h-[85vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold text-secondary">Detail Booking</h2>
          <button onClick={closeModal} className="text-gray-500 hover:text-gray-700">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        <div className="mb-4 pb-4 border-b">
          <div className="flex justify-between items-start mb-2">
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-2">Layanan:</h3>
              {selectedBooking.services && selectedBooking.services.length > 0 ? (
                <ul className="list-disc list-inside text-gray-700 ml-2 space-y-1">
                  {selectedBooking.services.map((service, index) => (
                    <li key={index} className="text-gray-700">{service}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-700">{selectedBooking.service || 'Layanan tidak tersedia'}</p>
              )}
            </div>
            <div>
              {getStatusBadge(selectedBooking.status)}
            </div>
          </div>
        </div>

        <div className="mb-4 pb-4 border-b">
          <h3 className="font-medium text-gray-900 mb-3">Informasi Kendaraan</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Merk</p>
              <p className="font-medium">{selectedBooking.vehicleMake || selectedBooking.make || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Model</p>
              <p className="font-medium">{selectedBooking.vehicleModel || selectedBooking.model || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Tahun</p>
              <p className="font-medium">{selectedBooking.vehicleYear || selectedBooking.year || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Plat Nomor</p>
              <p className="font-medium">{selectedBooking.vehicleLicensePlate || selectedBooking.license_plate || '-'}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4 pb-4 border-b">
          <div>
            <p className="text-sm text-gray-500">Tanggal</p>
            <p className="font-medium">{selectedBooking.date || 'Tidak tersedia'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Waktu</p>
            <p className="font-medium">{selectedBooking.time || 'Tidak tersedia'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Nama Pelanggan</p>
            <p className="font-medium">{selectedBooking.customerName || 'Tidak tersedia'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Montir</p>
            <p className="font-medium text-primary">{selectedBooking.mechanicName}</p>
          </div>
          <div className="col-span-2">
            <p className="text-sm text-gray-500">Total Biaya</p>
            <p className="font-medium text-primary">{formatPrice(selectedBooking.price || 0)}</p>
          </div>
        </div>

        <div className="mb-4 pb-4 border-b">
          <div className="flex items-start mb-2">
            <FiMapPin className="text-gray-500 mt-1 mr-2" />
            <div>
              <p className="text-sm text-gray-500">Lokasi</p>
              <p className="font-medium">{selectedBooking.location || 'Jl. Raya Serpong No. 123, Tangerang Selatan'}</p>
            </div>
          </div>

          {selectedBooking.notes && (
            <div className="flex items-start mt-3">
              <FiAlertTriangle className="text-yellow-500 mt-1 mr-2" />
              <div>
                <p className="text-sm text-gray-500">Catatan</p>
                <p className="font-medium">{selectedBooking.notes}</p>
              </div>
            </div>
          )}
        </div>

        <div className="text-sm text-gray-500 mb-5">
          <p>Dibuat pada: {new Date(selectedBooking.createdAt).toLocaleString('id-ID')}</p>
          {selectedBooking.paymentDate && (
            <p>Pembayaran: {new Date(selectedBooking.paymentDate).toLocaleString('id-ID')}</p>
          )}
          {selectedBooking.status === 'completed' && selectedBooking.completedAt && (
            <p>Selesai pada: {new Date(selectedBooking.completedAt).toLocaleString('id-ID')}</p>
          )}
          {selectedBooking.status === 'cancelled' && selectedBooking.canceledAt && (
            <p>Dibatalkan pada: {new Date(selectedBooking.canceledAt).toLocaleString('id-ID')}</p>
          )}
        </div>

        <div className="flex flex-wrap justify-end gap-2">
          <button
            onClick={() => handlePrintInvoice(selectedBooking)}
            className="inline-flex items-center rounded bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
          >
            <FiPrinter className="mr-2" />
            Cetak Invoice
          </button>

          {shouldShowPayButton(selectedBooking) && (
            <button
              onClick={() => handleRepayBooking(selectedBooking)}
              className="inline-flex items-center rounded bg-primary px-3 py-2 text-sm font-medium text-white hover:bg-primary-dark"
            >
              <FiCreditCard className="mr-2" />
              Bayar
            </button>
          )}

          {(selectedBooking.status === 'pending' || selectedBooking.status === 'confirmed') && (
            <button
              onClick={() => handleCancelBooking(selectedBooking.id)}
              className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
            >
              Batalkan Booking
            </button>
          )}
        </div>
      </div>
    );
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1); // Reset to first page when changing tabs
  };

  return (
    <DashboardLayout userRole="customer">
      <div className="mb-4 flex justify-between items-center">
        <div>
          <h1 className="text-xl font-bold text-secondary">Booking Saya</h1>
          <p className="text-sm text-gray-600">Lihat dan kelola booking servis Anda</p>
        </div>
        <Link
          to="/customer/book"
          className="bg-primary text-white px-3 py-1.5 rounded-lg hover:bg-primary-dark transition-colors flex items-center text-sm"
        >
          <FiPlus className="mr-1" size={16} /> Booking Baru
        </Link>
      </div>

      {/* Tabs */}
      <div className="mb-4 border-b">
        <nav className="flex flex-wrap -mb-px">
          <button
            className={`mr-6 py-2 px-1 font-medium text-xs border-b-2 ${activeTab === 'all'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            onClick={() => handleTabChange('all')}
          >
            Semua
          </button>
          <button
            className={`mr-6 py-2 px-1 font-medium text-xs border-b-2 ${activeTab === 'pending'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            onClick={() => handleTabChange('pending')}
          >
            Menunggu
          </button>
          <button
            className={`mr-6 py-2 px-1 font-medium text-xs border-b-2 ${activeTab === 'confirmed'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            onClick={() => handleTabChange('confirmed')}
          >
            Terjadwal
          </button>
          <button
            className={`mr-6 py-2 px-1 font-medium text-xs border-b-2 ${activeTab === 'in-progress'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            onClick={() => handleTabChange('in-progress')}
          >
            Diproses
          </button>
          <button
            className={`mr-6 py-2 px-1 font-medium text-xs border-b-2 ${activeTab === 'completed'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            onClick={() => handleTabChange('completed')}
          >
            Selesai
          </button>
          <button
            className={`py-2 px-1 font-medium text-xs border-b-2 ${activeTab === 'cancelled'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            onClick={() => handleTabChange('cancelled')}
          >
            Batal
          </button>
        </nav>
      </div>

      {/* Bookings list */}
      <div className="bg-white rounded-lg shadow-sm">
        {loading ? (
          <div className="p-6 text-center">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary mb-3"></div>
            <p className="text-gray-600 text-sm">Memuat data booking...</p>
          </div>
        ) : bookings.length === 0 ? (
          <div className="p-6 text-center">
            <div className="mb-3 flex justify-center">
              <div className="rounded-full bg-gray-100 p-2">
                <FiCalendar className="text-gray-400" size={20} />
              </div>
            </div>
            <h3 className="text-base font-medium text-gray-800 mb-2">Belum Ada Booking</h3>
            <p className="text-sm text-gray-600 mb-4">
              {activeTab === 'all'
                ? 'Anda belum memiliki booking servis apapun. Silakan buat booking servis baru.'
                : `Anda belum memiliki booking servis dengan status "${activeTab}".`}
            </p>
            <Link
              to="/customer/book"
              className="bg-primary text-white px-3 py-1.5 rounded-lg hover:bg-primary-dark transition-colors inline-flex items-center text-sm"
            >
              <FiPlus className="mr-1" size={16} /> Buat Booking Baru
            </Link>
          </div>
        ) : (
          <>
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-800">
                Daftar Booking {activeTab !== 'all' ? `(${activeTab})` : ''}
              </h3>
            </div>
            <div className="relative">
              <div className="overflow-x-auto max-h-[60vh] mb-2">
                {/* Optional message when table is scrollable */}
                <div className="text-xs text-gray-500 mb-1 text-right italic md:hidden">
                  <span>← Geser untuk melihat selengkapnya →</span>
                </div>

                {/* Tampilan mobile (hanya untuk layar kecil) */}
                <div className="md:hidden">
                  {bookings.map((booking) => (
                    <div key={booking.id} className="bg-white rounded-lg shadow-sm mb-4 border border-gray-200">
                      <div className="p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h3 className="font-medium text-gray-900">{booking.service}</h3>
                            <p className="text-sm text-gray-600">{formatPrice(booking.price)}</p>
                          </div>
                          <div>{getStatusBadge(booking.status)}</div>
                        </div>

                        <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-3 text-sm">
                          <div>
                            <p className="text-gray-500">Tanggal:</p>
                            <p className="font-medium">{booking.date}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Waktu:</p>
                            <p className="font-medium">{booking.time}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Nama:</p>
                            <p className="font-medium">{booking.customerName}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Merk:</p>
                            <p className="font-medium">{booking.vehicleMake || booking.make || '-'}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Model:</p>
                            <p className="font-medium">{booking.vehicleModel || booking.model || '-'}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Tahun:</p>
                            <p className="font-medium">{booking.vehicleYear || booking.year || '-'}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Plat Nomor:</p>
                            <p className="font-medium">{booking.vehicleLicensePlate || booking.license_plate || '-'}</p>
                          </div>
                        </div>

                        <div className="flex justify-end mt-2 pt-2 border-t border-gray-100">
                          <button
                            onClick={() => openBookingDetails(booking)}
                            className="text-primary hover:text-primary-dark font-medium"
                          >
                            Lihat Detail
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Tampilan desktop (tabel) */}
                <div className="hidden md:block w-full">
                  <table className="min-w-full divide-y divide-gray-200 table-fixed border-collapse text-sm">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        <th scope="col" className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[18%] bg-gray-50">
                          Layanan
                        </th>
                        <th scope="col" className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[12%] bg-gray-50">
                          Tanggal & Waktu
                        </th>
                        <th scope="col" className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[12%] bg-gray-50">
                          Status
                        </th>
                        <th scope="col" className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[10%] bg-gray-50">
                          Nama
                        </th>
                        <th scope="col" className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[8%] bg-gray-50">
                          Merk
                        </th>
                        <th scope="col" className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[8%] bg-gray-50">
                          Model
                        </th>
                        <th scope="col" className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[6%] bg-gray-50">
                          Tahun
                        </th>
                        <th scope="col" className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[8%] bg-gray-50">
                          Plat Nomor
                        </th>
                        <th scope="col" className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[8%] bg-gray-50">
                          Montir
                        </th>
                        <th scope="col" className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-[10%] bg-gray-50">
                          Tindakan
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {bookings.map((booking) => (
                        <tr key={booking.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-2 py-2">
                            <div className="text-sm font-medium text-gray-900 truncate">{booking.service}</div>
                            <div className="text-xs text-gray-500">{formatPrice(booking.price)}</div>
                          </td>
                          <td className="px-2 py-2">
                            <div className="text-sm text-gray-900">{booking.date}</div>
                            <div className="text-xs text-gray-500">{booking.time}</div>
                          </td>
                          <td className="px-2 py-2">
                            {getStatusBadge(booking.status)}
                          </td>
                          <td className="px-2 py-2 text-sm text-gray-900 font-medium truncate">
                            {booking.customerName}
                          </td>
                          <td className="px-2 py-2 text-sm text-gray-500 truncate">
                            {booking.vehicleMake || booking.make || '-'}
                          </td>
                          <td className="px-2 py-2 text-sm text-gray-500 truncate">
                            {booking.vehicleModel || booking.model || '-'}
                          </td>
                          <td className="px-2 py-2 text-sm text-gray-500">
                            {booking.vehicleYear || booking.year || '-'}
                          </td>
                          <td className="px-2 py-2 text-sm text-gray-500">
                            {booking.vehicleLicensePlate || booking.license_plate || '-'}
                          </td>
                          <td className="px-2 py-2 text-sm text-gray-500 truncate">
                            {booking.mechanicName}
                          </td>
                          <td className="px-2 py-2 text-right text-sm font-medium">
                            <button
                              onClick={() => openBookingDetails(booking)}
                              className="text-primary hover:text-primary-dark"
                            >
                              Detail
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Pagination controls */}
            <div className="border-t border-gray-200 px-3 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex items-center flex-wrap gap-2">
                <span className="text-xs text-gray-700 whitespace-nowrap">
                  Tampilkan
                </span>
                <select
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  className="border rounded p-1 text-xs"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-xs text-gray-700 whitespace-nowrap">
                  per halaman
                </span>
                <button
                  onClick={handleRefresh}
                  className="ml-1 p-1 text-gray-500 hover:text-primary rounded hover:bg-gray-100"
                  title="Refresh"
                >
                  <FiRefreshCw size={14} />
                </button>
              </div>

              {pagination.totalPages > 0 && (
                <div className="flex items-center flex-wrap justify-center sm:justify-end gap-1">
                  <span className="text-xs text-gray-700 mr-1 whitespace-nowrap">
                    {pagination.totalItems > 0 ? (
                      <>
                        <span className="font-medium">{(pagination.currentPage - 1) * pageSize + 1}</span>
                        {' - '}
                        <span className="font-medium">
                          {Math.min(pagination.currentPage * pageSize, pagination.totalItems)}
                        </span>
                        {' dari '}
                        <span className="font-medium">{pagination.totalItems}</span>
                      </>
                    ) : (
                      'Tidak ada data'
                    )}
                  </span>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={!pagination.hasPreviousPage}
                      className={`p-1 rounded ${pagination.hasPreviousPage
                          ? 'text-primary hover:bg-gray-100'
                          : 'text-gray-300 cursor-not-allowed'
                        }`}
                      aria-label="Previous page"
                    >
                      <FiChevronLeft size={16} />
                    </button>

                    <div className="flex items-center gap-1 flex-wrap">
                      {pagination.totalPages <= 5 ? (
                        // Show all pages if 5 or fewer
                        Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(page => (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`w-6 h-6 flex items-center justify-center text-xs rounded ${currentPage === page
                                ? 'bg-primary text-white'
                                : 'text-gray-700 hover:bg-gray-100'
                              }`}
                          >
                            {page}
                          </button>
                        ))
                      ) : (
                        // Show limited page numbers with ellipsis
                        <>
                          {/* First page */}
                          <button
                            onClick={() => handlePageChange(1)}
                            className={`w-6 h-6 flex items-center justify-center text-xs rounded ${currentPage === 1
                                ? 'bg-primary text-white'
                                : 'text-gray-700 hover:bg-gray-100'
                              }`}
                          >
                            1
                          </button>

                          {/* Ellipsis or page 2 */}
                          {currentPage > 3 && (
                            <span className="w-4 text-center text-gray-500 text-xs">...</span>
                          )}

                          {/* Pages around current page */}
                          {Array.from(
                            { length: Math.min(3, pagination.totalPages - 2) },
                            (_, i) => {
                              const pageNum = Math.max(
                                2,
                                Math.min(
                                  currentPage - 1 + i,
                                  pagination.totalPages - 1
                                )
                              );
                              return (
                                pageNum > 1 &&
                                pageNum < pagination.totalPages && (
                                  <button
                                    key={pageNum}
                                    onClick={() => handlePageChange(pageNum)}
                                    className={`w-6 h-6 flex items-center justify-center text-xs rounded ${currentPage === pageNum
                                        ? 'bg-primary text-white'
                                        : 'text-gray-700 hover:bg-gray-100'
                                      }`}
                                  >
                                    {pageNum}
                                  </button>
                                )
                              );
                            }
                          )}

                          {/* Ellipsis before last page */}
                          {currentPage < pagination.totalPages - 2 && (
                            <span className="w-4 text-center text-gray-500 text-xs">...</span>
                          )}

                          {/* Last page */}
                          <button
                            onClick={() => handlePageChange(pagination.totalPages)}
                            className={`w-6 h-6 flex items-center justify-center text-xs rounded ${currentPage === pagination.totalPages
                                ? 'bg-primary text-white'
                                : 'text-gray-700 hover:bg-gray-100'
                              }`}
                          >
                            {pagination.totalPages}
                          </button>
                        </>
                      )}
                    </div>

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={!pagination.hasNextPage}
                      className={`p-1 rounded ${pagination.hasNextPage
                          ? 'text-primary hover:bg-gray-100'
                          : 'text-gray-300 cursor-not-allowed'
                        }`}
                      aria-label="Next page"
                    >
                      <FiChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          {renderBookingModal()}
        </div>
      )}
    </DashboardLayout>
  );
};

export default CustomerBookings; 
